import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';

// More robust error handling
const originalConsoleError = console.error;
console.error = (...args) => {
  // Filter out certain React-specific warnings if needed
  if (typeof args[0] === 'string' && args[0].includes('Warning:')) {
    // Still log React warnings but don't propagate them as errors
    return originalConsoleError(...args);
  }
  return originalConsoleError(...args);
};

// Track if we're handling an error to prevent duplicate error reporting
let isHandlingError = false;

// Global error handler with improved handling
window.addEventListener('error', (event) => {
  // Prevent recursive error handling
  if (isHandlingError) {
    return;
  }
  
  isHandlingError = true;
  
  try {
    // Check if this is a "Script error" with limited information (cross-origin)
    if (event.message === "Script error." && !event.filename) {
      console.warn('Cross-origin script error suppressed');
      event.preventDefault();
      return;
    }
    
    // Ignore errors from external scripts
    if (event.filename && (
      event.filename.includes('googletagmanager.com') || 
      event.filename.includes('cookiebot.com') ||
      event.filename.includes('consent.cookiebot.com')
    )) {
      console.warn('External script error suppressed:', event.filename);
      event.preventDefault();
      return;
    }

    // Handle other errors with proper error information
    const errorInfo = {
      message: event.message || 'Unknown error',
      source: event.filename || 'Unknown source',
      lineNo: event.lineno || 'Unknown line',
      colNo: event.colno || 'Unknown column',
      stack: event.error?.stack || 'No stack trace available'
    };
    
    // Only log if we have meaningful information
    if (errorInfo.message !== 'Unknown error' || errorInfo.source !== 'Unknown source') {
      console.error('Global error caught:', errorInfo);
    }
  } catch (handlerError) {
    console.error('Error in error handler:', handlerError);
  } finally {
    isHandlingError = false;
    
    // Prevent the error from bubbling up further
    event.preventDefault();
  }
}, true);

// Global promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  // Prevent recursive error handling
  if (isHandlingError) {
    return;
  }
  
  isHandlingError = true;
  
  try {
    const reason = event.reason;
    
    // Create a meaningful error object
    const errorInfo = {
      reason: reason instanceof Error ? reason.message : String(reason || 'Unknown reason'),
      stack: reason instanceof Error ? reason.stack : 'No stack trace available'
    };
    
    console.error('Unhandled promise rejection:', errorInfo);
  } catch (handlerError) {
    console.error('Error in promise rejection handler:', handlerError);
  } finally {
    isHandlingError = false;
    
    // Prevent the rejection from bubbling up further
    event.preventDefault();
  }
});

// Initialize the app with error handling
try {
  const rootElement = document.getElementById('root');
  if (rootElement) {
    createRoot(rootElement).render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  } else {
    console.error('Root element not found');
  }
} catch (error) {
  console.error('Failed to render application:', error);
  // Show a fallback UI
  document.body.innerHTML = `
    <div style="padding: 20px; text-align: center;">
      <h2>Something went wrong</h2>
      <p>We're sorry, but the application failed to load. Please try refreshing the page.</p>
    </div>
  `;
}